import PropTypes from 'prop-types';
import React from 'react';

import MoreVert from '@mui/icons-material/MoreVert';
import { ListItem, Tooltip } from '@ublend-npm/aulaui-next';
import { evaluateMaterialAction } from '@core/materials/permissions';

import { UploadButton } from '../MaterialList/MaterialMenu.styled';
import { ENTER } from '../../../../../utils/keyCodes';

import {
  MATERIAL_IMPORTPAGE,
  MATERIAL_EXPORTPAGE,
  MATERIAL_DELETE,
  MATERIAL_FIND_PREVIOUS,
  MATERIAL_HIDE,
  MATERIAL_SECTION_MENU,
} from '../../../../../constants/texts';

import { Menu, IconButton } from './SectionViewActionMenu.styled';
import readUploadedFileContent from '../../../../../utils/readUploadedFileContent';

const MENU_ITEM_KEYS = {
  MATERIAL_EXPORTPAGE: 'MATERIAL_EXPORTPAGE',
  MATERIAL_IMPORTPAGE: 'MATERIAL_IMPORTPAGE',
  MATERIAL_FIND_PREVIOUS: 'MATERIAL_FIND_PREVIOUS',
  MATERIAL_DELETE: 'MATERIAL_DELETE',
  MATERIAL_HIDE: 'MATERIAL_HIDE',
};

const noop = () => null;
const initiateFileUpload = () => {
  document.getElementById('single-upload-button').click();
};

const getMenuItems = ({ excludeItems = [] }) =>
  [
    {
      key: MENU_ITEM_KEYS.MATERIAL_EXPORTPAGE,
      text: MATERIAL_EXPORTPAGE,
      id: 'material-export-button',
      fn: (props) => props.onExportMaterialPage,
      hideOnEdit: true,
    },
    {
      key: MENU_ITEM_KEYS.MATERIAL_IMPORTPAGE,
      text: MATERIAL_IMPORTPAGE,
      id: 'material-import-button',
      fn: () => initiateFileUpload,
      hideOnEdit: true,
    },
    {
      key: MENU_ITEM_KEYS.MATERIAL_FIND_PREVIOUS,
      text: MATERIAL_FIND_PREVIOUS,
      fn: (props) => props.onFindPreviousVersion,
    },
    {
      key: MENU_ITEM_KEYS.MATERIAL_DELETE,
      text: MATERIAL_DELETE,
      fn: (props) => props.onDeleteSection,
    },
  ].filter(({ key }) => !excludeItems.includes(key));

const handleFileUpload = async (event, onImportMaterialPage) => {
  const files = [...event.target.files];
  const fileContentPromises = files.map(readUploadedFileContent);
  const fileContents = await Promise.all(fileContentPromises);
  const materialContent = JSON.parse(fileContents[0]);
  onImportMaterialPage(materialContent);
  document.getElementById('single-upload-button').value = '';
};

const SectionViewActionMenu = (props) => {
  const {
    onHideSection,
    educatorOnly,
    canFindPreviousVersion,
    canExportMaterialPage,
    canEditMaterial = true,
    canDeleteMaterial = true,
    permissions = [],
    isRbacEnabled = false,
    isStudent = false,
  } = props;
  const excludeItems = [];
  if (!canFindPreviousVersion) {
    excludeItems.push(MENU_ITEM_KEYS.MATERIAL_FIND_PREVIOUS);
  }
  if (!canExportMaterialPage) {
    excludeItems.push(MENU_ITEM_KEYS.MATERIAL_EXPORTPAGE);
  }

  // In educator area: if user has educator area permission, they can do everything
  // Skip all permission checks when in educator area
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  // If in educator area and user has permission, allow all actions
  const allowAllInEducatorArea = educatorOnly && canEditMaterial;

  const hideStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('TOGGLE_VISIBILITY', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const importStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('IMPORT_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const exportStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('EXPORT_PAGE', {
        permissions: effectivePermissions,
        fallback: canExportMaterialPage,
      });
  const deleteStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('DELETE_PAGE', {
        permissions: effectivePermissions,
        fallback: canDeleteMaterial && Boolean(props.onDeleteSection),
      });
  const historyStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('VIEW_HISTORY', {
        permissions: effectivePermissions,
        fallback: canFindPreviousVersion && canEditMaterial,
      });

  const validMenuItems = [
    ...(onHideSection && !educatorOnly
      ? [
          {
            key: MENU_ITEM_KEYS.MATERIAL_HIDE,
            text: MATERIAL_HIDE,
            fn: (p) => p.onHideSection,
            status: hideStatus,
          },
        ]
      : []),
    ...getMenuItems({ excludeItems }).map((item) => {
      if (item.key === MENU_ITEM_KEYS.MATERIAL_EXPORTPAGE) {
        return { ...item, status: exportStatus };
      }
      if (item.key === MENU_ITEM_KEYS.MATERIAL_IMPORTPAGE) {
        return { ...item, status: importStatus };
      }
      if (item.key === MENU_ITEM_KEYS.MATERIAL_DELETE) {
        return { ...item, status: deleteStatus };
      }
      if (item.key === MENU_ITEM_KEYS.MATERIAL_FIND_PREVIOUS) {
        return { ...item, status: historyStatus };
      }
      return { ...item, status: { allowed: true } };
    }),
  ];

  // Students: only show menu items they have permission for (hide when not allowed)
  // Educators: show all items, disabled + tooltip when not allowed
  const filteredMenuItems = validMenuItems.filter((mItem) => {
    if (isStudent && isRbacEnabled) {
      return mItem.status.allowed;
    }
    return true;
  });

  const hasAnyPermission = filteredMenuItems.length > 0;
  const shouldShowMenu = isStudent ? isRbacEnabled && hasAnyPermission : true;

  if (!shouldShowMenu) {
    return null;
  }

  return (
    <div>
      <Menu
        menuListComponent="ul"
        menuListAttributes={{
          role: 'listbox',
          'aria-label': 'Section more actions',
        }}
        renderTarget={({ handleTargetClick }) => (
          <Tooltip
            title={MATERIAL_SECTION_MENU}
            position="bottom"
            onKeyDown={(e) => e.keyCode === ENTER && handleTargetClick(e)}
          >
            <div>
              <IconButton
                type="secondary"
                size="small"
                label={MATERIAL_SECTION_MENU}
                buttonAttributes={{ id: 'material-more-button' }}
                onClick={handleTargetClick}
                icon={MoreVert}
              />
            </div>
          </Tooltip>
        )}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onSelect={({ item }) => item()}
        renderChildren={({ getItemProps }) =>
          filteredMenuItems
            .filter((mItem) =>
              !props.editing ? mItem.hideOnEdit !== true : true
            )
            .map(({ key, text, id, fn, status = { allowed: true } }, index) => {
              const disabled = !status.allowed;
              const itemProps = getItemProps({
                item: disabled ? () => {} : fn(props),
              });
              const listItem = (
                <ListItem
                  size="medium"
                  key={key}
                  primaryText={text}
                  highlighted={index === 0}
                  disabled={disabled}
                  attributes={{
                    id,
                    'aria-label': text,
                    'aria-disabled': disabled,
                    'data-testid': key.toLowerCase(),
                  }}
                  {...itemProps}
                />
              );

              if (disabled && status.message) {
                return (
                  <Tooltip title={status.message} key={key} position="bottom">
                    <div>{listItem}</div>
                  </Tooltip>
                );
              }

              return listItem;
            })
        }
      />
      <UploadButton
        aria-hidden="true"
        type="file"
        id="single-upload-button"
        onChange={(event) =>
          handleFileUpload(event, props.onImportMaterialPage)
        }
      />
    </div>
  );
};

SectionViewActionMenu.propTypes = {
  canFindPreviousVersion: PropTypes.bool,
  canExportMaterialPage: PropTypes.bool,
  onExportMaterialPage: PropTypes.func,
  onImportMaterialPage: PropTypes.func,
  onHideSection: PropTypes.func,
  onFindPreviousVersion: PropTypes.func,
  onDeleteSection: PropTypes.func,
  editing: PropTypes.bool,
  educatorOnly: PropTypes.bool,
  canEditMaterial: PropTypes.bool,
  canDeleteMaterial: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  isStudent: PropTypes.bool,
};

SectionViewActionMenu.defaultProps = {
  canFindPreviousVersion: false,
  canExportMaterialPage: true,
  onExportMaterialPage: noop,
  onImportMaterialPage: noop,
  onHideSection: null,
  onFindPreviousVersion: noop,
  onDeleteSection: noop,
  editing: false,
  educatorOnly: false,
  canEditMaterial: true,
  canDeleteMaterial: true,
  permissions: [],
  isStudent: false,
};

export default SectionViewActionMenu;
